'use client';

import { Keyboard, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

export type ShortcutContext = 'form-invoice' | 'form-nir' | 'list-invoices' | 'list-recurring' | 'global';

export interface ShortcutEntry {
  label: string;
  keys: string[]; // e.g. ['⌘', 'S'] sau ['Ctrl', 'S'] (afișarea face conversia per OS)
  hint?: string; // text mic cu condiție (ex: "doar NIR")
}

export interface ShortcutGroup {
  title: string;
  entries: ShortcutEntry[];
}

interface KeyboardShortcutsModalProps {
  open: boolean;
  onClose: () => void;
  context: ShortcutContext;
}

// Returnează `⌘` pe Mac, `Ctrl` pe Windows/Linux. Default Ctrl pentru SSR.
function useModKey(): string {
  const [isMac, setIsMac] = useState(false);
  useEffect(() => {
    setIsMac(typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform));
  }, []);
  return isMac ? '⌘' : 'Ctrl';
}

// Configurația centralizată — ușor de extins
function getShortcutGroups(context: ShortcutContext, mod: string): ShortcutGroup[] {
  const globalGroup: ShortcutGroup = {
    title: 'Globale',
    entries: [
      { label: 'Caută rapid (Command Palette)', keys: [mod, 'K'] },
      { label: 'Asistență / Suport', keys: [mod, '/'] },
    ],
  };

  const commonGroup: ShortcutGroup = {
    title: 'Comune',
    entries: [
      { label: 'Închide modale / popovere', keys: ['Esc'] },
      { label: 'Afișează această listă', keys: ['?'] },
    ],
  };

  if (context === 'form-invoice') {
    return [
      globalGroup,
      {
        title: 'Pe acest formular',
        entries: [
          { label: 'Salvează ca ciornă', keys: [mod, 'S'] },
          { label: 'Emite / Trimite', keys: [mod, '↵'] },
          { label: 'Adaugă linie nouă', keys: [mod, '⇧', 'L'] },
        ],
      },
      commonGroup,
    ];
  }

  if (context === 'form-nir') {
    return [
      globalGroup,
      {
        title: 'Pe acest formular',
        entries: [
          { label: 'Salvează ca ciornă', keys: [mod, 'S'] },
          { label: 'Emite NIR', keys: [mod, '↵'] },
          { label: 'Adaugă linie nouă', keys: [mod, '⇧', 'L'] },
          { label: 'Șterge linia focusată (cu undo)', keys: [mod, '⌫'] },
        ],
      },
      commonGroup,
    ];
  }

  if (context === 'list-invoices') {
    return [
      globalGroup,
      {
        title: 'Pe această listă',
        entries: [
          { label: 'Focus filtru / căutare', keys: ['/'] },
          { label: 'Document nou', keys: ['N'] },
          { label: 'Reîncarcă', keys: ['R'] },
          { label: 'Export CSV', keys: ['E'] },
          { label: 'Șterge selectate', keys: ['⌫'] },
        ],
      },
      commonGroup,
    ];
  }

  if (context === 'list-recurring') {
    return [
      globalGroup,
      {
        title: 'Pe această listă',
        entries: [
          { label: 'Focus filtru', keys: ['/'] },
          { label: 'Recurență nouă', keys: ['N'] },
          { label: 'Reîncarcă', keys: ['R'] },
        ],
      },
      commonGroup,
    ];
  }

  return [globalGroup, commonGroup];
}

const kbdStyle: React.CSSProperties = {
  display: 'inline-block',
  fontFamily: 'JetBrains Mono, monospace',
  fontSize: '0.6875rem',
  fontWeight: 600,
  background: '#F4F4F3',
  border: '1px solid #EDEAE3',
  padding: '2px 7px',
  borderRadius: 5,
  color: '#3A3934',
  boxShadow: '0 1px 0 #E0DCD2',
  minWidth: 20,
  textAlign: 'center',
  marginLeft: 3,
};

export function KeyboardShortcutsModal({ open, onClose, context }: KeyboardShortcutsModalProps) {
  const mod = useModKey();
  const [mounted, setMounted] = useState(false);

  useEffect(() => { setMounted(true); }, []);

  if (!open || !mounted) return null;

  const groups = getShortcutGroups(context, mod);

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center p-4 pt-[12vh] bg-black/40 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full max-w-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-xl shadow-[0_20px_60px_rgba(15,23,42,.18),0_4px_12px_rgba(15,23,42,.08)] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-3 border-b border-neutral-200 dark:border-neutral-700">
          <Keyboard className="h-5 w-5 text-primary" />
          <h3 className="flex-1 text-base font-semibold text-neutral-800 dark:text-neutral-100">
            Scurtături tastatură
          </h3>
          <button
            onClick={onClose}
            className="flex h-6 w-6 items-center justify-center rounded-md text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 hover:text-neutral-700 dark:hover:text-neutral-200 transition-colors"
            title="Închide (Esc)"
            aria-label="Închide"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 max-h-[70vh] overflow-auto">
          {groups.map((group) => (
            <div key={group.title}>
              <div className="text-[0.6563rem] uppercase tracking-[0.12em] text-neutral-400 dark:text-neutral-500 font-semibold mb-2">
                {group.title}
              </div>
              <ul className="space-y-1">
                {group.entries.map((e) => (
                  <li
                    key={e.label}
                    className="flex items-center justify-between gap-2 text-sm text-neutral-700 dark:text-neutral-200 py-1 border-b border-dashed border-neutral-100 dark:border-neutral-800 last:border-b-0"
                  >
                    <span className="flex items-baseline gap-1.5 min-w-0">
                      <span className="truncate">{e.label}</span>
                      {e.hint && (
                        <span className="text-[0.625rem] uppercase tracking-wider text-neutral-400 shrink-0">
                          {e.hint}
                        </span>
                      )}
                    </span>
                    <span className="shrink-0">
                      {e.keys.map((k, i) => (
                        <span key={i} style={kbdStyle}>{k}</span>
                      ))}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Footer hint */}
        <div className="px-5 py-2.5 border-t border-neutral-200 dark:border-neutral-700 bg-neutral-50/60 dark:bg-neutral-900/60 text-[11px] text-neutral-500 dark:text-neutral-400 flex items-center justify-between">
          <span>Apasă <kbd style={{ ...kbdStyle, marginLeft: 0, fontSize: '0.625rem' }}>?</kbd> oricând să afișezi această listă.</span>
          <span>
            Închide <kbd style={{ ...kbdStyle, marginLeft: 4, fontSize: '0.625rem' }}>Esc</kbd>
          </span>
        </div>
      </div>
    </div>,
    document.body
  );
}
